import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Map from "../../Map/Map";
import Location from "../../Prospects/NewProspect/Location/Location";
import Button from "../../Common/FormElements/Button/Button";
import { updateProject } from "../../../REDUX/actions/appProjects";

/**
 * Slide 11 — Carte: the project's point, auto-geocoded from the Info projet
 * address — with a manual mode (shared Location editor: search to place,
 * drag to move) whose single point is stored on the project and wins over
 * the geocoded one.
 */
const ProjectLocation = ({ project }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { updateLoading } = useSelector((state) => state.appProjectsReducer);
  const [manualMode, setManualMode] = useState(false);
  const [manualMarkers, setManualMarkers] = useState(project.markers ?? []);
  const [geocoded, setGeocoded] = useState(null);

  const storedMarkers = project.markers ?? [];
  const address = [project.street, project.zip, project.city, project.country]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    // The manual point wins — only geocode when none is stored.
    if (storedMarkers.length || !address) {
      setGeocoded(null);
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            address
          )}.json?access_token=${
            process.env.REACT_APP_MAPBOX_TOKEN
          }&limit=1`,
          { signal: controller.signal }
        );
        const data = await response.json();
        setGeocoded(data?.features?.[0]?.center ?? null);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.log(error);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, storedMarkers.length]);

  const coordinates = storedMarkers[0]?.coordinates ?? geocoded ?? null;

  const handleSave = () => {
    dispatch(
      updateProject(project.id, { markers: manualMarkers.slice(0, 1) }, t, () =>
        setManualMode(false)
      )
    );
  };
  const handleReset = () => {
    setManualMarkers([]);
    dispatch(updateProject(project.id, { markers: [] }, t, () =>
      setManualMode(false)
    ));
  };

  return (
    <div className="flex-1 d-flex flex-column px-5 pt-4 pb-5">
      <div className="d-flex align-items-center gap-3 mb-3">
        <p className="f14 text-brightGrey mb-0 me-auto">
          {!address && !storedMarkers.length
            ? t(
                "Enter the project address in the Project info tab to see it on the map"
              )
            : storedMarkers.length
            ? t("Manually positioned point")
            : t("Position geocoded from the project address")}
        </p>
        {manualMode ? (
          <>
            <Button variant="outline" onClick={() => setManualMode(false)}>
              {t("Cancel")}
            </Button>
            {storedMarkers.length > 0 && (
              <Button variant="outline" onClick={handleReset}>
                {t("Reset to address")}
              </Button>
            )}
            <Button
              loading={updateLoading}
              disabled={!manualMarkers.length}
              onClick={updateLoading ? undefined : handleSave}
            >
              {t("Save")}
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            Icon={() => <i className="bi bi-geo-alt" />}
            onClick={() => {
              setManualMarkers(
                storedMarkers.length
                  ? storedMarkers
                  : geocoded
                  ? [{ coordinates: geocoded }]
                  : []
              );
              setManualMode(true);
            }}
          >
            {t("Position manually")}
          </Button>
        )}
      </div>

      {manualMode ? (
        <div style={{ height: 550 }}>
          <Location
            singleMarker
            initialValues={{
              markers: storedMarkers.length
                ? storedMarkers
                : geocoded
                ? [{ coordinates: geocoded }]
                : [],
            }}
            handleFormChange={(_, data) =>
              setManualMarkers(data?.markers ?? [])
            }
          />
        </div>
      ) : (
        <div style={{ height: 500 }}>
          <Map
            features={
              coordinates
                ? [
                    {
                      id: `project-${project.id}`,
                      firstName: project.name,
                      markers: [{ coordinates }],
                    },
                  ]
                : []
            }
            currentCenter={coordinates}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectLocation;
